import type { WebStrings } from "./strings.types.js";

function nfc(value: string): string {
  return value.normalize("NFC");
}

export const stringsKo = {
  metadata: {
    locale: "ko",
    title: "KlassShare",
    manifestName: "KlassShare",
    manifestShortName: "KlassShare",
    description: "KlassShare 교실 화면 공유 및 화이트보드 클라이언트",
    manifestDescription: "교실 화면 공유 및 화이트보드",
  },
  brand: {
    name: "KlassShare",
    homeAria: "KlassShare 처음으로",
    sessionAria: "KlassShare 수업 화면",
    currentSessionAria: "현재 수업",
    boardContext: (hostId) => `보드 ${hostId}`,
  },
  network: {
    online: "온라인",
    offline: "인터넷 연결 없음",
    offlineDetail:
      "캐시된 화면은 열 수 있지만 새로 참여하려면 공용 DNS와 인터넷 연결이 필요합니다. 이미 연결된 수업의 LAN 미디어는 계속 동작할 수 있습니다.",
  },
  landing: {
    eyebrow: "교실 화면에 연결",
    heading: "보드 코드를 입력하세요",
    intro:
      "StanbyME 또는 카메라가 없는 기기에서는 보드에 표시된 8자리 코드를 사용합니다.",
    hostCodeLabel: "8자리 보드 코드",
    hostCodeHint: "Crockford 코드 · I·L·O·U 제외",
    findBoard: "보드 찾기",
    qrFootnote:
      "휴대폰과 태블릿에서는 보드의 QR을 스캔하면 주소가 자동으로 설정됩니다.",
    invalidCodeFallback: "보드 코드를 확인해 주세요.",
    invalidQrFallback: "QR 참여 주소를 읽을 수 없습니다.",
  },
  roomDiscovery: {
    eyebrow: "교실 선택",
    heading: "이 네트워크의 교실",
    intro:
      "교실을 선택하면 해당 보드 한 대만 안전하게 확인한 뒤 참여 화면을 엽니다.",
    loading: "이 네트워크의 교실을 찾고 있어요…",
    empty:
      "이 네트워크에서 교실을 찾지 못했어요. QR 또는 8자리 코드로 참여하세요.",
    many: "이 네트워크에 교실이 너무 많아요. QR 또는 8자리 코드로 참여하세요.",
    unavailable:
      "교실 목록을 불러올 수 없어요. QR 또는 8자리 코드로 계속 참여할 수 있어요.",
    opaqueRoom: (hostId) => `등록된 보드 ${hostId}`,
    select: "선택",
    opaqueDetail: "선택하면 이 보드 한 대만 확인합니다.",
    checking: "확인 중…",
    reachable: "이 네트워크에 있음",
    stale: "현재 목록에서 확인되지 않음",
    staleDetail: "보드가 꺼졌거나 네트워크 연결이 끊겼을 수 있어요.",
    registeredUnreachable: "등록된 보드지만 연결되지 않아요",
    registeredUnreachableDetail:
      "보드가 꺼졌거나 인증서가 만료되었을 수 있어요.",
    guestVlan: "이 네트워크에서 연결할 수 없어요",
    guestVlanDetail:
      "게스트 와이파이일 수 있어요. 교실 와이파이로 바꾸거나 QR·코드를 사용하세요.",
    blocked: "이 브라우저에서 교실을 확인할 수 없어요",
    blockedDetail: "QR을 스캔하거나 8자리 코드를 입력하세요.",
    joinRoom: (hostId) => `${hostId} 교실에 참여`,
    fallbackHeading: "교실이 안 보이나요?",
    code: "8자리 코드 입력",
    revealedLabel: "보드가 공개한 교실 이름",
    nameUnavailable: "이름 확인 안 됨",
  },
  admission: {
    ariaLabel: "참여 보안 상태",
    grantVerified: {
      title: "참여 권한 확인됨",
      detail: "서명된 참여 권한과 교실 세션 확인이 모두 완료되었습니다.",
    },
    softFailUnavailable: {
      title: "클라우드 참여 권한 없이 계속",
      detail:
        "클라우드 참여 권한 서비스를 사용할 수 없어 제한된 가용성 경로로 진행합니다. 보드 확인은 계속 필요합니다.",
    },
    fallbackPending: {
      title: "직접 연결로 전환 중",
      detail:
        "클라우드 연결을 사용할 수 없어 보드 직접 연결을 시도하고 있습니다.",
    },
    establishedWanIndependent: {
      title: "교실 연결 유지 중",
      detail: "인터넷 연결이 끊겨도 이미 연결된 교실 세션은 계속됩니다.",
    },
    grantRequired: {
      title: "참여 권한 필요",
      detail: "이 교실은 서명된 참여 권한 없이는 새로 참여할 수 없습니다.",
    },
    seatCapExceeded: {
      title: "클라우드 참여 인원 한도 도달",
      detail: "현재 등급의 참여 권한 좌석이 모두 사용 중입니다.",
    },
    sessionStatementInvalid: {
      title: "보드 세션 확인 실패",
      detail: "보드가 보낸 세션 확인 정보를 검증하지 못했습니다.",
    },
    sessionMismatch: {
      title: "다른 교실 세션 감지",
      detail: "선택한 교실과 받은 참여 권한의 세션이 일치하지 않습니다.",
    },
    admissionDenied: {
      title: "교실 참여 거절됨",
      detail: "보드의 참여 정책에 따라 이 요청을 승인할 수 없습니다.",
    },
  },
  profile: {
    destination: (hostId) => `보드 ${hostId}에 참여`,
    changeBoardAria: "보드 코드 바꾸기",
    changeBoard: "바꾸기",
    displayNameLabel: "이름",
    displayNamePlaceholder: "이름을 입력하세요",
    resumeNote: "이 기기의 안전한 재연결 정보로 빠르게 복귀합니다.",
    join: "참여",
  },
  connection: {
    connecting: "보드에 연결하고 있습니다…",
    lnaPromptPending:
      "브라우저의 로컬 네트워크 접근 창에서 ‘허용’을 눌러 주세요.",
    reconnecting: "연결이 끊어져 자동으로 다시 연결하고 있습니다…",
    reconnectEscalated:
      "보드가 아직 켜지는 중인지 확인하세요. 브라우저가 로컬 네트워크 접근을 차단했다면 사이트 권한을 재설정하세요. 재연결은 계속 시도합니다.",
    checking: "연결 상태를 확인하고 있습니다…",
  },
  lna: {
    promptTitle: "브라우저 권한 확인",
    deniedTitle: "로컬 네트워크 접근이 차단되었습니다",
    deniedDetail: "브라우저 권한을 확인한 뒤 같은 화면에서 다시 시도하세요.",
    permissionConfirmed: "권한을 확인했습니다",
    browsers: {
      chrome: {
        label: "Chrome",
        steps: [
          "주소창 왼쪽의 사이트 정보에서 이 사이트의 권한을 여세요.",
          "로컬 네트워크 접근을 허용한 뒤 이 화면으로 돌아오세요.",
        ],
      },
      edge: {
        label: "Microsoft Edge",
        steps: [
          "주소창 왼쪽의 사이트 정보에서 ‘이 사이트에 대한 권한’을 여세요.",
          "로컬 네트워크 접근을 허용한 뒤 이 화면으로 돌아오세요.",
        ],
      },
      generic: {
        label: "현재 브라우저",
        steps: [
          "브라우저의 사이트 권한 화면을 여세요.",
          "이 사이트의 로컬 네트워크 접근을 허용한 뒤 다시 시도하세요.",
        ],
      },
      samsung: {
        label: "Samsung Internet",
        steps: [
          "브라우저 설정의 사이트 권한에서 이 사이트를 확인하세요.",
          "로컬 네트워크 접근 항목이 없으면 최신 Chrome 또는 Edge에서 다시 여세요.",
        ],
      },
      webos: {
        label: "webOS 브라우저",
        steps: [
          "TV 브라우저 또는 앱의 사이트 권한에서 로컬 네트워크 접근을 확인하세요.",
          "보드와 이 기기가 같은 Wi-Fi에 연결되어 있는지 확인하세요.",
        ],
      },
    },
  },
  recovery: {
    supersededTitle: "다른 연결에서 이 페이지를 사용하고 있어요",
    supersededDetail: "이 기기의 새 참여자 ID로 다시 연결하세요.",
    reconnectHere: "여기에서 다시 연결",
    terminalTitle: "이 보드에 참여할 수 없어요",
    returnToCode: "보드 코드 입력으로 돌아가기",
    stoppedTitle: "연결이 종료되었어요",
    stoppedDetail: "보드에서 수업을 종료했거나 이 연결을 내보냈습니다.",
    roomClosedLanding:
      "수업이 종료되었습니다. 다른 보드에 참여하려면 보드 코드를 입력하세요.",
  },
  session: {
    stageConnecting: "수업 화면 연결 중",
    stageReady: "수업 화면",
    sharedVideoAria: "공유 화면",
    emptyVideo: "공유 화면을 기다리는 중입니다.",
    emptyUnreachable: "영상 없이 화이트보드만",
    planeSwitchAria: "표시 화면 선택",
    videoAria: "화면 보기",
    video: "화면",
    whiteboardAria: "화이트보드 보기",
    whiteboard: "화이트보드",
    maximize: {
      enter: "전체화면",
      enterAria: "공유 화면 전체화면으로 보기",
      exit: "전체화면 해제",
      exitAria: "전체화면 해제",
    },
    statusRailAria: "연결 상태",
    presenterStarted: (presenterName?: string) => {
      const normalizedName =
        presenterName === undefined ? undefined : nfc(presenterName).trim();
      if (normalizedName === undefined || normalizedName.length === 0)
        return "발표자가 화면 공유를 시작했어요.";
      const namedPresenter = normalizedName.endsWith("님")
        ? normalizedName
        : `${normalizedName}님`;
      return `${namedPresenter}이 화면 공유를 시작했어요.`;
    },
    hostPresenterStarted: "이 보드가 화면 공유를 시작했어요.",
    presenterNameplate: (presenterName?: string) =>
      `발표 중: ${presenterName === undefined ? "발표자" : nfc(presenterName)}`,
    presenterFallback: "발표자",
    hostDisplayName: "이 보드",
    stageLoadingAria: "수업 화면을 불러오는 중",
    media: {
      presenterReconnecting: "발표자가 다시 연결하고 있습니다…",
      displayRecovering: "디스플레이를 다시 연결하고 있습니다…",
      trackMuted: "공유 화면이 잠시 멈췄습니다.",
    },
    transport: {
      direct: "빠른 직접 연결",
      relay: "보드를 통해 수신 중",
      unreachable: "영상 없이 화이트보드만",
    },
    roster: {
      summaryAria: (count) => `더보기 · 참여자 ${count}`,
      count: (count) => `참여자 ${count}`,
      more: "더보기",
      closeAria: "참여자 목록 닫기",
      loading: "참여자 정보를 불러오는 중",
      empty: "아직 참여자가 없어요",
      board: "보드",
      verifiedBoard: (hostId) => `인증된 보드 ${hostId}`,
      reconnecting: "재연결 중",
      connected: "연결됨",
      presenting: "발표 중",
    },
    share: {
      captureEndedByBoard: "보드에서 화면 공유를 종료했습니다.",
      captureEnded: "화면 공유 연결이 종료되었습니다.",
      captureEndedWithRoom: "수업이 종료되어 화면 공유가 멈췄습니다.",
      captureSuperseded: "다른 화면 공유로 전환되었습니다.",
      captureUnavailable: "화면 공유를 계속할 수 없습니다.",
      localActive: "내가 공유 중",
      selecting: "선택 중…",
      selectingDetail: "공유할 화면을 선택해 주세요.",
      needsSelection: "화면 선택 필요",
      needsSelectionDetail:
        "선생님이 승인했어요 · 누르면 화면 선택 창이 열립니다.",
      awaitingApproval: "승인 대기중…",
      awaitingApprovalDetail: "보드의 승인을 기다리고 있어요.",
      failed: "공유를 시작하지 못했어요",
      ready: "공유 준비됨",
      stop: "공유 중지",
      stopConfirmAria: "공유 중지 확인",
      stopQuestion: "공유를 중지할까요?",
      confirmStop: "중지 확인",
      continueSharing: "계속 공유",
      controlsAria: "수업 제어",
      myScreenAria: "내 화면 공유",
      myScreen: "내 화면 공유",
      startAria: "공유 시작",
      start: "공유 시작",
      selectingAria: "공유할 화면 선택 중",
      cancelRequest: "요청 취소",
      leaveAria: "수업 나가기",
      leave: "나가기",
    },
  },
  errors: {
    displayName: {
      reservedHost:
        "'host'는 교실 보드에 예약된 이름입니다. 다른 이름을 선택하세요.",
      codePointLength: "64자 이내의 표시할 이름을 입력하세요.",
      utf8Length:
        "UTF-8 인코딩 후 이름이 너무 깁니다. 더 짧은 이름을 사용하세요.",
      invalid: "올바른 표시 이름을 입력하세요.",
    },
    discovery: {
      hostCodeLength: "보드 코드는 8자리여야 합니다.",
      invalidHostCodeCharacter: "보드 코드에 사용할 수 없는 문자가 있습니다.",
      invalidLanIp: "QR의 LAN IP가 올바르지 않습니다.",
      privateLanIpRequired: "QR에는 사설 LAN IPv4 주소가 필요합니다.",
    },
    capture: {
      retryGesture: "공유 시작 버튼을 다시 눌러 주세요.",
      cancelled: "화면 선택이 취소되었습니다.",
      failed: "화면을 캡처할 수 없습니다.",
      failedWithName: (errorName) =>
        `화면을 캡처할 수 없습니다 (${errorName}).`,
    },
    shareRequest: {
      denied: "보드에서 공유 요청을 거절했습니다.",
      busy: "다른 발표자가 화면을 준비 중입니다.",
      unsupportedDevice: "이 기기에서는 화면 공유를 시작할 수 없습니다.",
      alreadySharing: "이미 이 화면을 공유하고 있습니다.",
      fallback: "화면 공유를 시작할 수 없습니다.",
    },
    signaling: {
      roomClosed: "수업이 종료되었습니다.",
    },
    terminal: {
      versionMismatch:
        "이 클라이언트 또는 보드를 업데이트해야 연결할 수 있습니다.",
      roomFull: "이 교실의 참여자 정원이 가득 찼습니다.",
      rateLimited: "참여 요청이 너무 많습니다. 잠시 후 다시 시도하세요.",
      licenseUnavailable:
        "현재 보드 라이선스로는 새 참여자를 받을 수 없습니다.",
      invalidPayload:
        "참여 정보가 올바르지 않습니다. 이름과 보드 코드를 확인하세요.",
      rejected: "보드가 참여를 거부했습니다.",
    },
  },
  whiteboard: {
    inputFailed: "화이트보드 입력에 실패했습니다.",
    textAddFailed: "텍스트를 추가하지 못했습니다.",
    viewAria: "협업 화이트보드",
    maximizeEnterAria: "화이트보드 전체화면으로 보기",
    addText: "텍스트 추가",
    drawingAreaAria: "공동 화이트보드 그리기 영역",
    chooseTextPositionAria: "텍스트를 놓을 위치 선택",
    chooseTextPosition: "캔버스를 눌러 텍스트 위치를 선택하세요",
    textLabel: "화이트보드 텍스트",
    textPlaceholder: "텍스트 입력",
    done: "완료",
    cancel: "취소",
    toolsAria: "화이트보드 도구",
    toolsToggleAria: "도구 모음 접기·펴기",
    drawingToolsAria: "그리기 도구",
    pen: "펜",
    highlighter: "형광펜",
    text: "텍스트",
    eraser: "지우개",
    color: "색상",
    width: (width) => `굵기 ${width}`,
    historyAria: "실행 취소 및 다시 실행",
    undoAria: "실행 취소",
    redoAria: "다시 실행",
    pagePreparing: "페이지 준비 중",
    pagePosition: (index, count) => `페이지 ${index} / ${count}`,
    localRecoveryReady: "로컬 복구 준비됨",
    localRecoveryPreparing: "로컬 복구 준비 중",
    synchronized: "동기화",
    reconnecting: "동기화 연결 중…",
    boardLocked: "보드가 입력을 잠갔어요",
    viewOnlyUnverified: "보기 전용 (터치 검증 전)",
    hostTimeSyncing: "보드 시간 동기화 중",
  },
} satisfies WebStrings;
